import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { EnergyDevice } from "../models/energyDevice";
import { DeviceEnergyResult, EnergyReport, combineEnergyReport } from "./combineEnergyReport";
import { fetchDeviceEnergy } from "./fetchDeviceEnergy";
import { useGeotabClient } from "@/components/GeotabClientContext";

/**
 * Reads the energy data for a set of vehicles over a period.
 *
 * One query per vehicle rather than one for the fleet. It costs nothing extra in requests — the
 * client batches the `Get` calls into `ExecuteMultiCall` regardless — and it buys three things
 * that matter for a report that takes minutes to fill: each vehicle appears as soon as its own
 * answer arrives, a vehicle that fails does so on its own row instead of taking the report with
 * it, and a period already read stays cached when the user switches views or narrows the vehicle
 * selection.
 *
 * @param devices - The vehicles to read.
 * @param fromDate - Start of the period.
 * @param toDate - End of the period.
 * @param enabled - Pass `false` until the period is known, so a stored period is not read after
 * the default one.
 * @param stopped - The user stopped the read; vehicles not read by then are marked as not read.
 * @returns See {@link EnergyReport}, plus `cancel`, which aborts the requests still running; pass
 * `enabled: false` with it so they are not started again.
 */
export function useEnergyReport(
  devices: EnergyDevice[],
  fromDate: Date,
  toDate: Date,
  enabled = true,
  stopped = false,
): EnergyReport & { cancel: () => void } {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const queryClient = useQueryClient();
  const cancel = useCallback(() => {
    void queryClient.cancelQueries({ queryKey: ["energyReport", activeSessionId] });
  }, [queryClient, activeSessionId]);

  // Memoised deliberately: React Query re-runs `combine` whenever its reference changes, and the
  // fleet-wide rows it builds would then be rebuilt on every keystroke in the grid filter.
  const combine = useCallback(
    (results: readonly DeviceEnergyResult[]) => combineEnergyReport(devices, results, stopped),
    [devices, stopped],
  );

  const report = useQueries({
    queries: devices.map(device => ({
      // The period goes in as a timestamp: React Query hashes the key by value, and two `Date`
      // objects for the same instant do not hash alike.
      queryKey: [
        "energyReport",
        activeSessionId,
        device.id,
        fromDate.getTime(),
        toDate.getTime(),
      ] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchDeviceEnergy(geotabClient, device, fromDate, toDate, signal),
      enabled,
      // The period is explicit, so the answer cannot go stale by itself. With the global defaults,
      // coming back to the tab after ten minutes would re-run the roughly six calls per vehicle
      // for the whole fleet.
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    })),
    combine,
  });

  return { ...report, cancel };
}
